<template>
  <DxPopup
    :visible="visible"
    @hiding="onHiding"
    @shown="popupShown"
    :width="665"
    :height="'auto'"
    :show-title="true"
    :title="popupTitle"
    :drag-enabled="false"
    :close-on-outside-click="false"
    :show-close-button="true"
    :wrapper-attr="{ class: 'css-v2 gc-popup-customer-balance-invoices' }"
  >
    <DxToolbarItem
      v-if="loaded"
      location="before"
      :html="`<span class='toolbar-title-secondary text-secondary'>${subtitle}</span>`"
    />

    <template #content>
      <div v-if="!loaded" class="popup-loading">
        <DxLoadIndicator :height="24" :width="24" />
      </div>

      <DxDataGrid
        v-else
        :data-source="invoices"
        :show-borders="true"
        :show-column-lines="true"
        :show-row-lines="true"
        :column-auto-width="true"
        :columns="columns"
        :paging="{ enabled: false }"
        :pager="{ visible: false }"
        :column-chooser="{ enabled: false }"
        :search-panel="{ visible: false }"
      >
        <template #Status="{ data }">
          <GcStatusChip :status="data.data.Status" v-if="data.data.Status" />
        </template>

        <DxSummary>
          <DxTotalItem column="InvoiceNumber" summary-type="count" display-format="Total" />
          <DxTotalItem
            column="Amount"
            summary-type="sum"
            :value-format="decimalFormat"
            display-format="{0}"
          />
          <DxTotalItem
            column="Balance"
            summary-type="sum"
            :value-format="decimalFormat"
            display-format="{0}"
          />
        </DxSummary>
      </DxDataGrid>
    </template>
  </DxPopup>
</template>

<script>
import { DxPopup, DxToolbarItem } from "devextreme-vue/popup";
import { DxDataGrid, DxSummary, DxTotalItem } from "devextreme-vue/data-grid";
import { DxLoadIndicator } from "devextreme-vue/load-indicator";
import { CustomerBalanceService } from "../../_services/api/customers/customer-balance.service";
import { getDecimalFormat } from "../../_store/helpers";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";

export default {
  name: "GcPopupCustomerBalanceInvoices",
  components: { DxPopup, DxToolbarItem, DxDataGrid, DxSummary, DxTotalItem, DxLoadIndicator },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    customerCode: {
      type: String,
      required: true,
    },
    termType: {
      type: String,
      required: true,
    },
    term: {
      type: String,
      required: true,
    },
    baseCurrencyCode: {
      type: String,
      required: true,
    },
    bucketLabel: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loaded: false,
      invoices: [],
    };
  },
  computed: {
    decimalFormat() {
      return getDecimalFormat("Decimal-16-2").ReadOnlyFormat;
    },
    popupTitle() {
      return this.bucketLabel || "Invoices";
    },
    totalAmount() {
      return this.invoices.reduce((sum, invoice) => sum + (invoice.Amount || 0), 0);
    },
    subtitle() {
      const count = this.invoices.length;
      const formattedTotal = this.totalAmount.toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      return `${count} invoice${count === 1 ? "" : "s"} · $${formattedTotal}`;
    },
    columns() {
      return [
        {
          caption: "Invoice #",
          dataField: "InvoiceNumber",
          width: ColumnWidth.InvoiceNumber.width + 20,
          minWidth: ColumnWidth.InvoiceNumber.minWidth + 20,
        },
        {
          caption: "Invoice Date",
          dataField: "InvoiceDate",
          dataType: "date",
          format: "dd MMM yyyy",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
        },
        {
          caption: "Due Date",
          dataField: "DueDate",
          dataType: "date",
          format: "dd MMM yyyy",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
        },
        {
          caption: `Amount (${this.baseCurrencyCode})`,
          dataField: "Amount",
          alignment: "right",
          format: this.decimalFormat,
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
        },
        {
          caption: `Balance (${this.baseCurrencyCode})`,
          dataField: "Balance",
          alignment: "right",
          format: this.decimalFormat,
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
        },
        {
          caption: "Status",
          dataField: "Status",
          cellTemplate: "Status",
          width: ColumnWidth.Status.width,
          minWidth: ColumnWidth.Status.minWidth,
        },
      ];
    },
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        this.loaded = false;
        this.invoices = [];
      }
    },
  },
  methods: {
    onHiding() {
      this.$emit("update:visible", false);
    },
    async popupShown() {
      const response = await CustomerBalanceService.getInvoices(
        this.customerCode,
        this.termType,
        this.term,
      );

      this.invoices = response.data;
      this.loaded = true;
    },
  },
};
</script>

<style lang="scss">
.gc-popup-customer-balance-invoices {
  .toolbar-title-secondary {
    padding-left: unset !important;
    font-weight: 400;
    font-size: 14px;
  }

  .popup-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 0;
  }
}
</style>
